'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';

// ─── Types ───────────────────────────────────────────────────────────────────
type Lang = 'en' | 'ar';
type Theme = 'dark' | 'light';

interface PricingPlan {
  name: string;
  nameAr: string;
  price: number;
  period: string;
  periodAr: string;
  description: string;
  descriptionAr: string;
  features: string[];
  featuresAr: string[];
  cta: string;
  ctaAr: string;
  highlighted: boolean;
  badge?: string;
  badgeAr?: string;
}

interface Testimonial {
  name: string;
  role: string;
  roleAr: string;
  company: string;
  quote: string;
  quoteAr: string;
  initials: string;
  color: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const PRICING_PLANS: PricingPlan[] = [
  {
    name: 'Individual',
    nameAr: 'فردي',
    price: 5,
    period: '/mo',
    periodAr: '/شهر',
    description: 'Perfect for solo professionals who need to stay on top of their day.',
    descriptionAr: 'مثالي للمحترفين الأفراد الذين يحتاجون إلى تنظيم يومهم.',
    features: ['1 workspace seat', 'Daily planner & focus mode', 'Task tracking & reminders', 'Progress reports', 'Email support'],
    featuresAr: ['مقعد واحد في الفضاء', 'المخطط اليومي ووضع التركيز', 'تتبع المهام والتذكيرات', 'تقارير التقدم', 'دعم عبر البريد الإلكتروني'],
    cta: 'Start Free Trial',
    ctaAr: 'ابدأ التجربة المجانية',
    highlighted: false,
  },
  {
    name: 'Small Team',
    nameAr: 'فريق صغير',
    price: 15,
    period: '/mo',
    periodAr: '/شهر',
    description: 'Built for growing teams that need coordination without the chaos.',
    descriptionAr: 'مصمم للفرق النامية التي تحتاج إلى تنسيق بدون فوضى.',
    features: ['Up to 5 seats', 'Everything in Individual', 'Team analytics dashboard', 'Role-based permissions', 'Task assignment & comments', 'Priority support'],
    featuresAr: ['حتى 5 مقاعد', 'كل ما في الخطة الفردية', 'لوحة تحليلات الفريق', 'صلاحيات حسب الدور', 'تعيين المهام والتعليقات', 'دعم ذو أولوية'],
    cta: 'Start Free Trial',
    ctaAr: 'ابدأ التجربة المجانية',
    highlighted: true,
    badge: 'Most Popular',
    badgeAr: 'الأكثر شيوعاً',
  },
  {
    name: 'Medium Team',
    nameAr: 'فريق متوسط',
    price: 29,
    period: '/mo',
    periodAr: '/شهر',
    description: 'For established teams that demand full visibility and control.',
    descriptionAr: 'للفرق الراسخة التي تتطلب رؤية كاملة وتحكماً تاماً.',
    features: ['Up to 10 seats', 'Everything in Small Team', 'Advanced time management', 'MRR & billing insights', 'Custom workspace branding', 'Dedicated account manager'],
    featuresAr: ['حتى 10 مقاعد', 'كل ما في الفريق الصغير', 'إدارة وقت متقدمة', 'رؤى MRR والفواتير', 'علامة تجارية مخصصة', 'مدير حساب مخصص'],
    cta: 'Contact Sales',
    ctaAr: 'تواصل مع المبيعات',
    highlighted: false,
  },
];

const TESTIMONIALS: Testimonial[] = [
  {
    name: 'Sarah Al-Rashidi',
    role: 'Product Manager',
    roleAr: 'مديرة منتج',
    company: 'TechVentures MENA',
    quote: 'DayFlow replaced three tools we were juggling. Our team ships 40% faster now — not because of magic, but because everyone finally knows what to work on.',
    quoteAr: 'حلّ DayFlow محل ثلاثة أدوات كنا نستخدمها. فريقنا الآن أسرع بنسبة 40% — ليس سحراً، بل لأن الجميع يعرف ما يجب العمل عليه.',
    initials: 'SR',
    color: '#6C63FF',
  },
  {
    name: 'Omar Khalil',
    role: 'Engineering Lead',
    roleAr: 'قائد هندسة',
    company: 'Buildstack',
    quote: 'The focus mode alone is worth the subscription. I block two hours every morning and get more done than I used to in a full day.',
    quoteAr: 'وضع التركيز وحده يستحق الاشتراك. أحجز ساعتين كل صباح وأنجز أكثر مما كنت أنجزه في يوم كامل.',
    initials: 'OK',
    color: '#22D3EE',
  },
  {
    name: 'Lina Mansour',
    role: 'Operations Director',
    roleAr: 'مديرة العمليات',
    company: 'Nexora Group',
    quote: 'We onboarded 8 people in under 10 minutes. The permission system is smart — team leaders see exactly what they need, nothing more.',
    quoteAr: 'أضفنا 8 أشخاص في أقل من 10 دقائق. نظام الصلاحيات ذكي — يرى قادة الفريق ما يحتاجونه فقط، لا أكثر.',
    initials: 'LM',
    color: '#F59E0B',
  },
];

const STATS = [
  { value: '12,000+', labelEn: 'Active Users', labelAr: 'مستخدم نشط' },
  { value: '98%', labelEn: 'Uptime SLA', labelAr: 'ضمان التشغيل' },
  { value: '4.9★', labelEn: 'Average Rating', labelAr: 'متوسط التقييم' },
  { value: '3 min', labelEn: 'Avg. Onboarding', labelAr: 'متوسط التهيئة' },
];

// ─── Theme helpers ────────────────────────────────────────────────────────────
function themeClasses(theme: Theme) {
  return {
    bg: theme === 'dark' ? 'bg-[#0F1117]' : 'bg-white',
    bgAlt: theme === 'dark' ? 'bg-[#0A0C12]' : 'bg-gray-50',
    bgCard: theme === 'dark' ? 'bg-[#1A1D27]' : 'bg-white',
    bgCardGrad: theme === 'dark' ?'linear-gradient(145deg, #1A1D27 0%, #1E2130 100%)' :'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
    border: theme === 'dark' ? 'border-[#2A2F45]' : 'border-gray-200',
    text: theme === 'dark' ? 'text-[#E8EAED]' : 'text-gray-900',
    textMuted: theme === 'dark' ? 'text-[#A0A8C0]' : 'text-gray-500',
    textFaint: theme === 'dark' ? 'text-[#6B7280]' : 'text-gray-400',
    navBg: theme === 'dark' ? 'bg-[#0F1117]/90 border-[#2A2F45]' : 'bg-white/90 border-gray-200',
    inputBg: theme === 'dark' ? '#252A3A' : '#F3F4F6',
    statsBg: theme === 'dark' ? 'bg-[#1A1D27]/50' : 'bg-gray-50',
    footerBg: theme === 'dark' ? 'bg-[#0F1117]' : 'bg-gray-50',
  };
}

// ─── NavBar ───────────────────────────────────────────────────────────────────
function NavBar({ lang, setLang, theme, setTheme }: {
  lang: Lang; setLang: (l: Lang) => void;
  theme: Theme; setTheme: (t: Theme) => void;
}) {
  const [scrolled, setScrolled] = useState(false);
  const tc = themeClasses(theme);
  const isAr = lang === 'ar';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = isAr
    ? [{ label: 'المميزات', href: '#features' }, { label: 'الأسعار', href: '#pricing' }, { label: 'آراء العملاء', href: '#testimonials' }]
    : [{ label: 'Features', href: '#features' }, { label: 'Pricing', href: '#pricing' }, { label: 'Testimonials', href: '#testimonials' }];

  return (
    <nav
      dir={isAr ? 'rtl' : 'ltr'}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? `backdrop-blur-xl border-b ${tc.navBg}` : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Image
            src="/assets/images/app_logo.png"
            alt="DayFlow Logo"
            width={32}
            height={32}
            className="rounded-lg"
            priority
          />
          <span className={`font-bold text-lg tracking-tight ${tc.text}`}>DayFlow</span>
        </div>

        {/* Links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((item) => (
            <a key={item.label} href={item.href} className={`text-sm ${tc.textMuted} hover:${tc.text} transition-colors duration-200`}>
              {item.label}
            </a>
          ))}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          {/* Language toggle */}
          <button
            onClick={() => setLang(isAr ? 'en' : 'ar')}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all duration-200 ${tc.border} ${tc.textMuted} hover:${tc.text}`}
          >
            {isAr ? 'EN' : 'عربي'}
          </button>

          {/* Theme toggle */}
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-all duration-200 ${tc.border} ${tc.textMuted} hover:${tc.text}`}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          <Link
            href="/login"
            className={`text-sm ${tc.textMuted} hover:${tc.text} transition-colors duration-200 hidden sm:block`}
          >
            {isAr ? 'تسجيل الدخول' : 'Sign In'}
          </Link>
          <Link
            href="/login?tab=signup"
            className="text-sm font-semibold px-4 py-2 rounded-lg text-white transition-all duration-200 hover:opacity-90 hover:-translate-y-px"
            style={{ background: 'linear-gradient(135deg, #6C63FF 0%, #22D3EE 100%)' }}
          >
            {isAr ? 'ابدأ مجاناً' : 'Get Started Free'}
          </Link>
        </div>
      </div>
    </nav>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function HeroSection({ lang, theme }: { lang: Lang; theme: Theme }) {
  const tc = themeClasses(theme);
  const isAr = lang === 'ar';

  return (
    <section dir={isAr ? 'rtl' : 'ltr'} className={`relative min-h-screen flex items-center overflow-hidden pt-16 ${tc.bg}`}>
      {/* Animated background */}
      <div className={`absolute inset-0 ${tc.bg}`}>
        <div
          className="absolute top-[-20%] left-[10%] w-[600px] h-[600px] rounded-full opacity-20 blur-[120px]"
          style={{ background: 'radial-gradient(circle, #6C63FF 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-[-10%] right-[5%] w-[500px] h-[500px] rounded-full opacity-15 blur-[100px]"
          style={{ background: 'radial-gradient(circle, #22D3EE 0%, transparent 70%)' }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(#6C63FF 1px, transparent 1px), linear-gradient(90deg, #6C63FF 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-center">
        {/* Copy */}
        <div className="space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#6C63FF]/30 bg-[#6C63FF]/10 text-[#A89FFF] text-xs font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-[#6C63FF] animate-pulse" />
            {isAr ? 'إدارة المهام، بشكل مختلف' : 'Task Management, Reimagined'}
          </div>

          <h1 className={`text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight ${tc.text}`}>
            {isAr ? (
              <>
                توقف عن إدارة المهام.
                <br />
                <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #6C63FF 0%, #22D3EE 100%)' }}>
                  ابدأ بإنجازها.
                </span>
              </>
            ) : (
              <>
                Stop managing tasks.
                <br />
                <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #6C63FF 0%, #22D3EE 100%)' }}>
                  Start finishing them.
                </span>
              </>
            )}
          </h1>

          <p className={`text-lg leading-relaxed max-w-lg ${tc.textMuted}`}>
            {isAr
              ? 'يمنح DayFlow فريقك مكاناً واحداً لتخطيط اليوم وتتبع التقدم والتركيز — مع ضوابط ذكية للأدوار حتى يعمل الجميع على ما يهم.' :'DayFlow gives your team a single place to plan the day, track progress, and stay focused — with smart role controls so everyone works on what matters.'}
          </p>

          <div className="flex flex-wrap gap-4">
            <Link
              href="/login?tab=signup"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-sm transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 shadow-lg"
              style={{ background: 'linear-gradient(135deg, #6C63FF 0%, #22D3EE 100%)', boxShadow: '0 8px 32px rgba(108, 99, 255, 0.35)' }}
            >
              {isAr ? 'ابدأ مجاناً — بدون بطاقة' : 'Start Free — No Card Needed'}
              <svg className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <a
              href="#features"
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-sm border transition-all duration-200 ${tc.border} ${tc.textMuted} hover:border-[#6C63FF]/50 hover:${tc.text}`}
            >
              {isAr ? 'كيف يعمل؟' : 'See how it works'}
            </a>
          </div>

          {/* Social proof */}
          <div className="flex items-center gap-4 pt-2">
            <div className="flex -space-x-2">
              {['#6C63FF', '#22D3EE', '#F59E0B', '#10B981'].map((color, i) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full border-2 border-[#0F1117] flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: color }}
                >
                  {['S', 'O', 'L', 'M'][i]}
                </div>
              ))}
            </div>
            <p className={`text-sm ${tc.textMuted}`}>
              <span className={`font-semibold ${tc.text}`}>12,000+</span>{' '}
              {isAr ? 'فريق يستخدم DayFlow بالفعل' : 'teams already use DayFlow'}
            </p>
          </div>
        </div>

        {/* Dashboard mockup */}
        <div className="relative hidden lg:block">
          <div
            className={`rounded-2xl border overflow-hidden ${tc.border}`}
            style={{
              background: theme === 'dark' ? 'linear-gradient(145deg, #1A1D27 0%, #1E2130 100%)' : 'linear-gradient(145deg, #f8f9ff 0%, #ffffff 100%)',
              boxShadow: '0 40px 80px rgba(0,0,0,0.3), 0 0 0 1px rgba(108,99,255,0.1)',
            }}
          >
            <div className={`flex items-center gap-2 px-4 py-3 border-b ${tc.border}`}>
              <div className="w-3 h-3 rounded-full bg-[#EF4444]/70" />
              <div className="w-3 h-3 rounded-full bg-[#F59E0B]/70" />
              <div className="w-3 h-3 rounded-full bg-[#10B981]/70" />
              <div className={`flex-1 mx-4 h-5 rounded flex items-center px-3`} style={{ background: theme === 'dark' ? '#252A3A' : '#E5E7EB' }}>
                <span className={`text-xs ${tc.textFaint}`}>app.day-flow.cloud/daily-planner</span>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className={`font-semibold text-sm ${tc.text}`}>{isAr ? 'صباح الخير، سارة 👋' : 'Good morning, Sarah 👋'}</div>
                  <div className={`text-xs mt-0.5 ${tc.textFaint}`}>{isAr ? 'الأحد، 3 مايو — 4 مهام اليوم' : 'Sunday, May 3 — 4 tasks due today'}</div>
                </div>
                <div className="px-3 py-1 rounded-lg text-xs font-medium text-white" style={{ background: 'linear-gradient(135deg, #6C63FF, #22D3EE)' }}>
                  {isAr ? 'وضع التركيز' : 'Focus Mode'}
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className={tc.textMuted}>{isAr ? 'التقدم اليومي' : 'Daily Progress'}</span>
                  <span className="text-[#6C63FF] font-semibold">3 / 7</span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: theme === 'dark' ? '#252A3A' : '#E5E7EB' }}>
                  <div className="h-full rounded-full" style={{ width: '43%', background: 'linear-gradient(90deg, #6C63FF, #22D3EE)' }} />
                </div>
              </div>
              <div className="space-y-2">
                {[
                  { label: isAr ? 'مراجعة خارطة طريق Q2' : 'Review Q2 roadmap', done: true, priority: isAr ? 'عالي' : 'high' },
                  { label: isAr ? 'اجتماع الفريق اليومي' : 'Team standup call', done: true, priority: isAr ? 'متوسط' : 'medium' },
                  { label: isAr ? 'تحديث لوحة السبرينت' : 'Update sprint board', done: true, priority: isAr ? 'منخفض' : 'low' },
                  { label: isAr ? 'كتابة ملاحظات الإصدار' : 'Write release notes', done: false, priority: isAr ? 'عالي' : 'high' },
                  { label: isAr ? 'مراجعة ملاحظات العميل' : 'Client feedback review', done: false, priority: isAr ? 'متوسط' : 'medium' },
                ].map((task, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: task.done ? 'transparent' : (theme === 'dark' ? '#252A3A' : '#F3F4F6') }}>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${task.done ? 'border-[#10B981] bg-[#10B981]' : (theme === 'dark' ? 'border-[#2A2F45]' : 'border-gray-300')}`}>
                      {task.done && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-xs flex-1 ${task.done ? `${tc.textFaint} line-through` : tc.text}`}>{task.label}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 pt-1">
                {[
                  { label: isAr ? 'سلسلة' : 'Streak', value: '12d', color: '#F59E0B' },
                  { label: isAr ? 'ساعات تركيز' : 'Focus hrs', value: '2.4h', color: '#22D3EE' },
                  { label: isAr ? 'مكتمل' : 'Completed', value: '94%', color: '#10B981' },
                ].map((stat, i) => (
                  <div key={i} className="p-2.5 rounded-lg text-center" style={{ background: theme === 'dark' ? '#252A3A' : '#F3F4F6' }}>
                    <div className="text-sm font-bold" style={{ color: stat.color }}>{stat.value}</div>
                    <div className={`text-[10px] mt-0.5 ${tc.textFaint}`}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="absolute -bottom-4 -left-4 px-4 py-3 rounded-xl border flex items-center gap-3" style={{ background: theme === 'dark' ? '#1A1D27' : '#ffffff', borderColor: theme === 'dark' ? '#2A2F45' : '#E5E7EB', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <div className="w-8 h-8 rounded-lg bg-[#0D2B22] flex items-center justify-center">
              <svg className="w-4 h-4 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <div className={`text-xs font-semibold ${tc.text}`}>{isAr ? 'سرعة الفريق ارتفعت' : 'Team velocity up'}</div>
              <div className="text-[10px] text-[#10B981]">{isAr ? '+38% هذا الشهر' : '+38% this month'}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Stats Bar ────────────────────────────────────────────────────────────────
function StatsBar({ lang, theme }: { lang: Lang; theme: Theme }) {
  const tc = themeClasses(theme);
  const isAr = lang === 'ar';
  return (
    <section dir={isAr ? 'rtl' : 'ltr'} className={`border-y ${tc.border} ${tc.statsBg}`}>
      <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-8">
        {STATS.map((stat) => (
          <div key={stat.labelEn} className="text-center">
            <div className="text-3xl font-bold bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #6C63FF 0%, #22D3EE 100%)' }}>
              {stat.value}
            </div>
            <div className={`text-sm mt-1 ${tc.textFaint}`}>{isAr ? stat.labelAr : stat.labelEn}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Features ─────────────────────────────────────────────────────────────────
function FeaturesSection({ lang, theme }: { lang: Lang; theme: Theme }) {
  const tc = themeClasses(theme);
  const isAr = lang === 'ar';
  return (
    <section id="features" dir={isAr ? 'rtl' : 'ltr'} className={`py-28 ${tc.bg}`}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-xl mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#22D3EE]/30 bg-[#22D3EE]/10 text-[#22D3EE] text-xs font-medium mb-4">
            {isAr ? 'مبني للعمل الحقيقي' : 'Built for real work'}
          </div>
          <h2 className={`text-4xl font-bold leading-tight mb-4 ${tc.text}`}>
            {isAr ? (
              <>كل ما يحتاجه فريقك،<br /><span className={tc.textFaint}>لا أكثر ولا أقل.</span></>
            ) : (
              <>Everything your team needs,<br /><span className={tc.textFaint}>nothing they don&apos;t.</span></>
            )}
          </h2>
          <p className={tc.textMuted}>
            {isAr
              ? 'DayFlow مصمم خصيصاً للفرق القائمة على المهام. لا تضخيم، لا ميزات زائدة — فقط الأدوات التي تدفع العمل للأمام.'
              : "DayFlow is purpose-built for task-driven teams. No bloat, no feature creep — just the tools that move work forward."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Daily Planner — large */}
          <div className={`md:col-span-2 p-7 rounded-2xl border relative overflow-hidden group hover:border-[#6C63FF]/40 transition-all duration-300 ${tc.border}`} style={{ background: tc.bgCardGrad }}>
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-0 group-hover:opacity-10 transition-opacity duration-500 blur-3xl" style={{ background: '#6C63FF' }} />
            <div className="w-10 h-10 rounded-xl bg-[#6C63FF]/15 flex items-center justify-center mb-5">
              <svg className="w-5 h-5 text-[#6C63FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${tc.text}`}>{isAr ? 'المخطط اليومي' : 'Daily Planner'}</h3>
            <p className={`text-sm leading-relaxed mb-6 max-w-sm ${tc.textMuted}`}>
              {isAr
                ? 'ابدأ كل يوم بخطة واضحة. اسحب المهام إلى جدولك، حدد كتل الوقت، وانهِ اليوم وأنت تعرف ما تم إنجازه.' :'Start each day with a clear plan. Drag tasks into your schedule, set time blocks, and end the day knowing exactly what got done.'}
            </p>
            <div className="space-y-2">
              {(isAr
                ? ['9:00 ص — مراجعة التصميم', '11:00 ص — تخطيط السبرينت', '2:00 م — مكالمة العميل']
                : ['9:00 AM — Design review', '11:00 AM — Sprint planning', '2:00 PM — Client call']
              ).map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <div className="w-1 h-8 rounded-full" style={{ background: ['#6C63FF', '#22D3EE', '#F59E0B'][i] }} />
                  <span className={tc.textMuted}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Focus Mode */}
          <div className={`p-7 rounded-2xl border relative overflow-hidden group hover:border-[#22D3EE]/40 transition-all duration-300 ${tc.border}`} style={{ background: tc.bgCardGrad }}>
            <div className="w-10 h-10 rounded-xl bg-[#22D3EE]/15 flex items-center justify-center mb-5">
              <svg className="w-5 h-5 text-[#22D3EE]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${tc.text}`}>{isAr ? 'وضع التركيز' : 'Focus Mode'}</h3>
            <p className={`text-sm leading-relaxed ${tc.textMuted}`}>
              {isAr
                ? 'ركّز على مهمة واحدة في كل مرة. مؤقت بومودورو، حجب المشتتات، وسجلات الجلسات — حتى يحدث العمل العميق فعلاً.' :'Lock in on one task at a time. Pomodoro-style timer, distraction blocking, and session logs — so deep work actually happens.'}
            </p>
            <div className="mt-5 flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: theme === 'dark' ? '#252A3A' : '#E5E7EB' }}>
                <div className="h-full w-[65%] rounded-full bg-[#22D3EE]" />
              </div>
              <span className="text-xs text-[#22D3EE] font-mono">25:00</span>
            </div>
          </div>

          {/* Team Analytics */}
          <div className={`p-7 rounded-2xl border relative overflow-hidden group hover:border-[#F59E0B]/40 transition-all duration-300 ${tc.border}`} style={{ background: tc.bgCardGrad }}>
            <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center mb-5">
              <svg className="w-5 h-5 text-[#F59E0B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${tc.text}`}>{isAr ? 'تحليلات الفريق' : 'Team Analytics'}</h3>
            <p className={`text-sm leading-relaxed ${tc.textMuted}`}>
              {isAr
                ? 'اعرف من يعاني من الضغط، ومن لديه وقت فراغ، وأين تتشكل الاختناقات — قبل أن تصبح مشاكل.' : "See who's overloaded, who's idle, and where bottlenecks form — before they become problems."}
            </p>
            <div className="mt-5 flex items-end gap-1.5 h-12">
              {[40, 65, 50, 80, 55, 90, 70].map((h, i) => (
                <div key={i} className="flex-1 rounded-sm" style={{ height: `${h}%`, background: i === 5 ? '#F59E0B' : (theme === 'dark' ? '#252A3A' : '#E5E7EB') }} />
              ))}
            </div>
          </div>

          {/* Role Permissions — large */}
          <div className={`md:col-span-2 p-7 rounded-2xl border relative overflow-hidden group hover:border-[#10B981]/40 transition-all duration-300 ${tc.border}`} style={{ background: tc.bgCardGrad }}>
            <div className="w-10 h-10 rounded-xl bg-[#10B981]/15 flex items-center justify-center mb-5">
              <svg className="w-5 h-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${tc.text}`}>{isAr ? 'صلاحيات حسب الدور' : 'Role-Based Permissions'}</h3>
            <p className={`text-sm leading-relaxed mb-6 max-w-md ${tc.textMuted}`}>
              {isAr
                ? 'تحكم ثلاثي المستويات: المشرف العام، قائد الفريق، والعضو. يرى الجميع ما يحتاجه بالضبط — ولا شيء غير ذلك.' : "Three-tier access control: Super Admin, Team Leader, and Member. Everyone sees exactly what they need — and nothing they shouldn't."}
            </p>
            <div className="grid grid-cols-3 gap-3">
              {[
                {
                  role: isAr ? 'المشرف العام' : 'Super Admin',
                  color: '#6C63FF',
                  perms: isAr
                    ? ['جميع الفضاءات', 'التحكم بالفواتير', 'تعليق الحسابات']
                    : ['All workspaces', 'Billing control', 'Suspend accounts'],
                },
                {
                  role: isAr ? 'قائد الفريق' : 'Team Leader',
                  color: '#22D3EE',
                  perms: isAr
                    ? ['دعوة الأعضاء', 'تعيين المهام', 'عرض التحليلات']
                    : ['Invite members', 'Assign tasks', 'View analytics'],
                },
                {
                  role: isAr ? 'العضو' : 'Member',
                  color: '#10B981',
                  perms: isAr
                    ? ['مهامي', 'المخطط اليومي', 'تقارير التقدم']
                    : ['Own tasks', 'Daily planner', 'Progress reports'],
                },
              ].map((r) => (
                <div key={r.role} className="p-3 rounded-xl" style={{ background: theme === 'dark' ? '#252A3A' : '#F3F4F6' }}>
                  <div className="text-xs font-semibold mb-2" style={{ color: r.color }}>{r.role}</div>
                  <ul className="space-y-1">
                    {r.perms.map((p) => (
                      <li key={p} className={`flex items-center gap-1.5 text-[10px] ${tc.textMuted}`}>
                        <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: r.color }} />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────────
function PricingSection({ lang, theme }: { lang: Lang; theme: Theme }) {
  const tc = themeClasses(theme);
  const isAr = lang === 'ar';
  return (
    <section id="pricing" dir={isAr ? 'rtl' : 'ltr'} className={`py-28 ${tc.bgAlt} relative overflow-hidden`}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] opacity-10 blur-[100px]" style={{ background: 'radial-gradient(ellipse, #6C63FF 0%, transparent 70%)' }} />
      <div className="relative max-w-6xl mx-auto px-6">
        <div className="text-center max-w-xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#6C63FF]/30 bg-[#6C63FF]/10 text-[#A89FFF] text-xs font-medium mb-4">
            {isAr ? 'أسعار بسيطة وشفافة' : 'Simple, transparent pricing'}
          </div>
          <h2 className={`text-4xl font-bold mb-4 ${tc.text}`}>
            {isAr ? 'ادفع فقط لما يستخدمه فريقك' : 'Pay for what your team actually uses'}
          </h2>
          <p className={tc.textMuted}>
            {isAr ? 'جميع الخطط تشمل تجربة مجانية 14 يوماً. لا حاجة لبطاقة ائتمان للبدء.' : 'All plans include a 14-day free trial. No credit card required to start.'}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {PRICING_PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`relative p-7 rounded-2xl border transition-all duration-300 hover:-translate-y-1 ${plan.highlighted ? 'border-[#6C63FF]/60' : `${tc.border} hover:border-[#6C63FF]/30`}`}
              style={{
                background: plan.highlighted
                  ? (theme === 'dark' ? 'linear-gradient(145deg, #1E1B3A 0%, #1A1D27 100%)' : 'linear-gradient(145deg, #f0eeff 0%, #f8f9ff 100%)')
                  : tc.bgCardGrad,
                boxShadow: plan.highlighted ? '0 0 0 1px rgba(108,99,255,0.3), 0 20px 60px rgba(108,99,255,0.15)' : 'none',
              }}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg, #6C63FF, #22D3EE)' }}>
                  {isAr ? plan.badgeAr : plan.badge}
                </div>
              )}
              <div className="mb-6">
                <div className={`text-sm font-medium mb-1 ${tc.textMuted}`}>{isAr ? plan.nameAr : plan.name}</div>
                <div className="flex items-baseline gap-1">
                  <span className={`text-4xl font-bold ${tc.text}`}>${plan.price}</span>
                  <span className={`text-sm ${tc.textFaint}`}>{isAr ? plan.periodAr : plan.period}</span>
                </div>
                <p className={`text-xs mt-2 leading-relaxed ${tc.textFaint}`}>{isAr ? plan.descriptionAr : plan.description}</p>
              </div>
              <ul className="space-y-3 mb-8">
                {(isAr ? plan.featuresAr : plan.features).map((feature) => (
                  <li key={feature} className={`flex items-start gap-2.5 text-sm ${tc.textMuted}`}>
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: plan.highlighted ? '#6C63FF' : '#10B981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <Link
                href="/login?tab=signup"
                className={`block w-full text-center py-3 rounded-xl text-sm font-semibold transition-all duration-200 hover:opacity-90 hover:-translate-y-px ${plan.highlighted ? 'text-white' : `${tc.text} border ${tc.border} hover:border-[#6C63FF]/50`}`}
                style={plan.highlighted ? { background: 'linear-gradient(135deg, #6C63FF 0%, #22D3EE 100%)' } : {}}
              >
                {isAr ? plan.ctaAr : plan.cta}
              </Link>
            </div>
          ))}
        </div>
        <p className={`text-center text-xs mt-8 ${tc.textFaint}`}>
          {isAr ? 'جميع الأسعار بالدولار الأمريكي. إلغاء في أي وقت. المقاعد لكل فضاء عمل.' : 'All prices in USD. Cancel anytime. Seats are per workspace.'}
        </p>
      </div>
    </section>
  );
}

// ─── Testimonials ─────────────────────────────────────────────────────────────
function TestimonialsSection({ lang, theme }: { lang: Lang; theme: Theme }) {
  const tc = themeClasses(theme);
  const isAr = lang === 'ar';
  return (
    <section id="testimonials" dir={isAr ? 'rtl' : 'ltr'} className={`py-28 ${tc.bg}`}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-xl mx-auto mb-16">
          <h2 className={`text-4xl font-bold mb-4 ${tc.text}`}>
            {isAr ? 'من انتقل، بقي.' : 'Teams that switched, stayed.'}
          </h2>
          <p className={tc.textMuted}>
            {isAr ? 'آراء حقيقية من فرق حقيقية — ليست اقتباسات تسويقية منتقاة.' : 'Real feedback from real teams — not cherry-picked marketing quotes.'}
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className={`p-7 rounded-2xl border transition-all duration-300 ${tc.border}`} style={{ background: tc.bgCardGrad }}>
              <div className="text-4xl font-serif leading-none mb-4" style={{ color: t.color, opacity: 0.4 }}>&ldquo;</div>
              <p className={`text-sm leading-relaxed mb-6 ${tc.textMuted}`}>{isAr ? t.quoteAr : t.quote}</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: t.color }}>
                  {t.initials}
                </div>
                <div>
                  <div className={`text-sm font-semibold ${tc.text}`}>{t.name}</div>
                  <div className={`text-xs ${tc.textFaint}`}>{isAr ? t.roleAr : t.role} · {t.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CTA ──────────────────────────────────────────────────────────────────────
function CTASection({ lang, theme }: { lang: Lang; theme: Theme }) {
  const tc = themeClasses(theme);
  const isAr = lang === 'ar';
  return (
    <section dir={isAr ? 'rtl' : 'ltr'} className={`py-28 ${tc.bgAlt} relative overflow-hidden`}>
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 50% at 50% 50%, #6C63FF, transparent)' }} />
      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <h2 className={`text-5xl font-bold mb-6 leading-tight ${tc.text}`}>
          {isAr ? (
            <>أفضل أعمال فريقك<br />تبدأ غداً.</>
          ) : (
            <>Your team&apos;s best work<br />starts tomorrow.</>
          )}
        </h2>
        <p className={`text-lg mb-10 max-w-lg mx-auto ${tc.textMuted}`}>
          {isAr
            ? 'انضم إلى أكثر من 12,000 فريق توقفوا عن الغرق في المهام وبدأوا في تحقيق النتائج. تجربة مجانية، لا بطاقة مطلوبة.'
            : "Join 12,000+ teams who stopped drowning in tasks and started shipping results. Free trial, no card required."}
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link
            href="/login?tab=signup"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-white font-semibold transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #6C63FF 0%, #22D3EE 100%)', boxShadow: '0 8px 32px rgba(108, 99, 255, 0.4)' }}
          >
            {isAr ? 'ابدأ التجربة المجانية' : 'Start Free Trial'}
            <svg className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
          <Link
            href="/login"
            className={`inline-flex items-center gap-2 px-8 py-4 rounded-xl font-medium border transition-all duration-200 ${tc.border} ${tc.textMuted} hover:border-[#6C63FF]/50 hover:${tc.text}`}
          >
            {isAr ? 'تسجيل الدخول' : 'Sign In'}
          </Link>
        </div>
        <p className={`text-xs mt-6 ${tc.textFaint}`}>
          {isAr ? '14 يوم تجربة مجانية · بدون بطاقة · إلغاء في أي وقت' : '14-day free trial · No credit card · Cancel anytime'}
        </p>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer({ lang, theme }: { lang: Lang; theme: Theme }) {
  const tc = themeClasses(theme);
  const isAr = lang === 'ar';
  return (
    <footer dir={isAr ? 'rtl' : 'ltr'} className={`border-t ${tc.border} ${tc.footerBg} py-10`}>
      <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Image src="/assets/images/app_logo.png" alt="DayFlow Logo" width={24} height={24} className="rounded-md" />
          <span className={`text-sm font-semibold ${tc.text}`}>DayFlow</span>
          <span className={`text-sm ml-2 ${tc.textFaint}`}>© 2026</span>
        </div>
        <div className={`flex items-center gap-6 text-xs ${tc.textFaint}`}>
          <a href="#features" className={`hover:${tc.textMuted} transition-colors`}>{isAr ? 'المميزات' : 'Features'}</a>
          <a href="#pricing" className={`hover:${tc.textMuted} transition-colors`}>{isAr ? 'الأسعار' : 'Pricing'}</a>
          <Link href="/login" className={`hover:${tc.textMuted} transition-colors`}>{isAr ? 'تسجيل الدخول' : 'Sign In'}</Link>
          <a href="mailto:support@day-flow.cloud" className={`hover:${tc.textMuted} transition-colors`}>{isAr ? 'الدعم' : 'Support'}</a>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [lang, setLang] = useState<Lang>('ar');
  const [theme, setTheme] = useState<Theme>('dark');

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'bg-[#0F1117]' : 'bg-white'}`}
      style={{ fontFamily: 'var(--font-dm-sans), sans-serif' }}
    >
      <NavBar lang={lang} setLang={setLang} theme={theme} setTheme={setTheme} />
      <HeroSection lang={lang} theme={theme} />
      <StatsBar lang={lang} theme={theme} />
      <FeaturesSection lang={lang} theme={theme} />
      <PricingSection lang={lang} theme={theme} />
      <TestimonialsSection lang={lang} theme={theme} />
      <CTASection lang={lang} theme={theme} />
      <Footer lang={lang} theme={theme} />
    </div>
  );
}
